import { usePermissions } from "helpers/usePermission";
import React, { act, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { ModalTypes } from "configs/types";
import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";

import CmsPromoEditModal from "modals/cms/promo/CmsPromoEditModal";
import AddToTournamentModal from "modals/gameCenter/action/AddToTournamentModal";
import LiveOpsTournamentsEditModal from "modals/liveOps/tournaments/LiveOpsTournamentsEditModal";
import LiveOpsCashbackGamesEditModal from "modals/liveOps/cashbackGames/LiveOpsCashbackGamesEditModal";
import CmsPagesEditModal from "modals/cms/pages/list/CmsPagesEditModal";
import CmsPagesSeoEditModal from "modals/cms/pages/seo/CmsPagesSeoEditModal";
import CmsPagesFaqEditModal from "modals/cms/pages/faq/CmsPagesFaqEditModal";
import LiveOpsPromocodesEditModal from "modals/liveOps/promocodes/LiveOpsPromocodesEditModal";
import LiveOpsCashbacksEditModal from "modals/liveOps/cashbacks/LiveOpsCashbacksEditModal";
import PremiumModal from "modals/PremiumModal";
import CmsBannersEditModal from "modals/cms/banners/CmsBannersEditModal";
import CreateWithdrawal from "modals/players/action/CreateWithdrawal";
import CreateDeposit from "modals/players/action/CreateDeposit";
import CreateCompensation from "modals/players/action/CreateCompensation";
import SelectPlayerModal from "modals/payments/action/SelectPlayerModal";
import CreateConfiscation from "modals/players/action/CreateConfiscation";
import PaymentProvidersEditModal from "modals/payment/psp/providers/PaymentProvidersEditModal";
import PaymentMethodsEditModal from "modals/payment/psp/methods/PaymentMethodsEditModal";
import PaymentTransactionsOperationsEditModal from "modals/payment/transactions/operations/PaymentTransactionsOperationsEditModal";
import GameCenterProviderEditModal from "modals/gameCenter/provider/GameCenterProviderEditModal";
import NotificationSendModal from "modals/notifications/NotificationSendModal";
import NotificationsEditModal from "modals/notifications/NotificationsEditModal";
import NotificationEmailEditModal from "modals/notifications/email/NotificationEmailEditModal";
import LiveOpsLoyaltyPointsEditModal from "modals/liveOps/loyaltyProgram/LiveOpsLoyaltyPointsEditModal";
import LiveOpsDepositPointLimitModal from "modals/liveOps/loyaltyProgram/LiveOpsDepositPointLimitModal";
import LiveOpsLoyaltyLevelEditModal from "modals/liveOps/loyaltyProgram/LiveOpsLoyaltyLevelEditModal";
import AddToWagerModal from "modals/gameCenter/action/AddToWagerModal";
import AddToCashbackModal from "modals/gameCenter/action/AddToCashbackModal";
import LiveOpsBonusesEditModal from "modals/liveOps/bonuses/LiveOpsBonusesEditModal";
import LiveOpsWagerGamesEditModal from "modals/liveOps/wagerGames/LiveOpsWagerGamesEditModal";
import PlayersListEditModal from "modals/players/list/PlayersListEditModal";
import AddToSegmentModal from "modals/players/action/AddToSegmentModal";
import PlayersSegmentEditModal from "modals/players/segments/PlayersSegmentEditModal";
import GameCenterCommonModal from "modals/gameCenter/GameCenterCommonModal";
import MultiScopeModal from "modals/gameCenter/multiScope/MultiScopeModal";
import AddGameModal from "modals/gameCenter/action/AddGameModal";
import GameCenterGamesEditModal from "modals/gameCenter/games/GameCenterGamesEditModal";
import GameSessionModal from "modals/gameCenter/GameSessionModal";
import Header from "./Header/Header";
import Sidebar from "./Sidebar/Sidebar";
import Notifications from "./Notifications/Notifications";
import ZonesEditModal from "modals/domains/zones/ZonesEditModal";
import MirrorEditModal from "modals/domains/mirrors/MirrorEditModal";
import MirrorCreateModal from "modals/domains/mirrors/MirrorCreateModal";
import { GuideModal } from "modals/guides/Guide";
import CountriesModal from "modals/domains/countries/CountriesModal";
import PartnersModal from "modals/cms/footer/partners/CmsFooterPartnersEditModal";
import ApplicationsModal from "modals/cms/footer/applications/CmsFooterAppsEditModal";
import MainTextModal from "modals/cms/footer/mainText/CmsFooterMainTextEditModal";
import PaymentModal from "modals/cms/footer/payments/CmsFooterPaymentsEditModal";
import BrandCoreModal from "modals/cms/footer/brandCore/CmsFooterBrandCoreEditModal";
import ChannelsModal from "modals/support/Channels/ChannelsModal";
import ThreadModal from "modals/support/Treads/ThreadsModal";
import CmsFooterSocialNetworksEditModal from "modals/cms/footer/socialNetworks/CmsFooterSocialNetworksEditModal";
import RiskPermissionEditModal from "modals/risk/permissions/RiskPermissionEditModal";
import RiskLimitEditModal from "modals/risk/limits/RiskLimitEditModal";
import { ContentPreviewModal } from "modals/shared/ContentPreviewModal/ContentPreviewModal";
import CmsCustomizedEditModal from "modals/cms/customized/CmsCustomizedEditModal";
import PlayersPresetsEditModal from "modals/players/presets/PlayersPresetsEditModal";
import RiskKycPresetsEditModal from 'modals/risk/kyc/presets/RiskKycPresetsEditModal';
import RiskKycRulesEditModal from 'modals/risk/kyc/rules/RiskKycRulesEditModal';
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { BackOfficeRealtimeProvider } from "realtime/BackOfficeRealtimeProvider";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SchedulerModal } from "modals/scheduler/SchedulerModal";

type Props = {
  children: React.ReactNode;
};

const Layout: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();

  const activeModal = useAppSelector((store) => store.modals.activeModal);
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = usePermissionHelper();
  const withdrawalPermissions = usePermissions("payments_transactions_withdrawals");
  const canCreate = permissions?.create ?? false;
  const [searchParams] = useSearchParams();

  const handleCloseModal = () => {
    navigate(location.pathname);
    dispatch(closeModal());
  };
  useEffect(() => {
    const overlay = document.querySelector(".overlay");
    if (activeModal && overlay) {
      overlay.addEventListener("click", handleCloseModal);
      return () => {
        overlay.removeEventListener("click", handleCloseModal);
      };
    }
  }, [activeModal]);

  const renderModal = () => {
    if (activeModal && activeModal === ModalTypes.gameCenter) {
      return <GameCenterCommonModal />;
    }
    if (activeModal && activeModal === ModalTypes.multiScope) {
      return <MultiScopeModal />;
    }
    if (activeModal && activeModal === ModalTypes.addGames) {
      return <AddGameModal />;
    }
    if (activeModal && activeModal === ModalTypes.editGame) {
      return <GameCenterGamesEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.gameSession) {
      return <GameSessionModal />;
    }
    if (activeModal && activeModal === ModalTypes.segment) {
      return <PlayersSegmentEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.player) {
      return <PlayersListEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.addToSegment) {
      return <AddToSegmentModal />;
    }
    if (activeModal && activeModal === ModalTypes.wager) {
      return <LiveOpsWagerGamesEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.addGamesToWager) {
      return <AddToWagerModal />;
    }
    if (activeModal && activeModal === ModalTypes.bonus) {
      const isExistingBonus = searchParams.get("entityId") !== null;
      const canOpenBonus = isExistingBonus
        ? permissions?.read
        : permissions?.create;

      return canOpenBonus ? <LiveOpsBonusesEditModal /> : null;
    }
    if (activeModal && activeModal === ModalTypes.loyaltyLevel) {
      return <LiveOpsLoyaltyLevelEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.depositPointLimit) {
      const isExistingLimit = searchParams.get("entityId") !== null;
      const canOpenLimit = isExistingLimit
        ? permissions?.read
        : permissions?.create;
      return canOpenLimit ? <LiveOpsDepositPointLimitModal /> : null;
    }
    if (activeModal && activeModal === ModalTypes.loyaltyPoints) {
      const isExistingPointsRule = searchParams.get("entityId") !== null;
      const canOpenPointsRule = isExistingPointsRule
        ? permissions?.read
        : permissions?.create;

      return canOpenPointsRule ? <LiveOpsLoyaltyPointsEditModal /> : null;
    }
    if (activeModal && activeModal === ModalTypes.notification) {
      return <NotificationsEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.notificationSend) {
      return <NotificationSendModal />;
    }
    if (activeModal && activeModal === ModalTypes.banners) {
      return <CmsBannersEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.provider) {
      return <GameCenterProviderEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.paymentProvider) {
      return <PaymentProvidersEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.paymentMethod) {
      return <PaymentMethodsEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.operationsDetails) {
      return <PaymentTransactionsOperationsEditModal />;
    }
    if (activeModal === ModalTypes.createWithdrawal) return withdrawalPermissions?.includes("update") ? <CreateWithdrawal key={searchParams.get("entityId")} /> : null;
    if (activeModal && activeModal === ModalTypes.createDeposit) {
      return canCreate ? <CreateDeposit /> : null;
    }
    if (activeModal && activeModal === ModalTypes.createCompensation) {
      return canCreate ? <CreateCompensation /> : null;
    }
    if (activeModal && activeModal === ModalTypes.selectPlayer) {
      return canCreate ? <SelectPlayerModal /> : null;
    }
    if (activeModal && activeModal === ModalTypes.createConfiscation) {
      return canCreate ? <CreateConfiscation /> : null;
    }
    if (activeModal && activeModal === ModalTypes.premium) {
      return <PremiumModal />;
    }
    if (activeModal && activeModal === ModalTypes.promoCode) {
      return <LiveOpsPromocodesEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.cashback) {
      const isExistingCashback = searchParams.get("entityId") !== null;
      const canOpenCashback = isExistingCashback
        ? permissions?.read
        : permissions?.create;

      return canOpenCashback ? <LiveOpsCashbacksEditModal /> : null;
    }
    if (activeModal && activeModal === ModalTypes.page) {
      return <CmsPagesEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.seo) {
      return <CmsPagesSeoEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.faq) {
      return <CmsPagesFaqEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.cashbackGames) {
      return <LiveOpsCashbackGamesEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.addGamesToCashback) {
      return <AddToCashbackModal />;
    }
    if (activeModal && activeModal === ModalTypes.tournaments) {
      return <LiveOpsTournamentsEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.addGamesToTournament) {
      return <AddToTournamentModal />;
    }
    if (activeModal && activeModal === ModalTypes.promo) {
      return <CmsPromoEditModal />;
    }

    if (activeModal && activeModal === ModalTypes.email) {
      return <NotificationEmailEditModal />;
    }

    if (activeModal && activeModal === ModalTypes.zone) {
      return <ZonesEditModal />;
    }

    if (activeModal && activeModal === ModalTypes.mirrorEdit) {
      return <MirrorEditModal />;
    }

    if (activeModal && activeModal === ModalTypes.mirrorCreate) {
      return <MirrorCreateModal />;
    }

    if (activeModal && activeModal === ModalTypes.guide) {
      return <GuideModal />;
    }

    if (activeModal && activeModal === ModalTypes.country) {
      return <CountriesModal />;
    }

    if (activeModal && activeModal === ModalTypes.partner) {
      return <PartnersModal />;
    }
    if (activeModal && activeModal === ModalTypes.socialNetwork) {
      return <CmsFooterSocialNetworksEditModal />;
    }

    if (activeModal && activeModal === ModalTypes.applications) {
      return <ApplicationsModal />;
    }

    if (activeModal && activeModal === ModalTypes.mainText) {
      return <MainTextModal />;
    }

    if (activeModal && activeModal === ModalTypes.payments) {
      return <PaymentModal />;
    }

    if (activeModal && activeModal === ModalTypes.brandCore) {
      return <BrandCoreModal />;
    }
    if (activeModal && activeModal === ModalTypes.channels) {
      return <ChannelsModal />;
    }
    if (activeModal && activeModal === ModalTypes.threads) {
      return <ThreadModal />;
    }
    if (activeModal && activeModal === ModalTypes.riskPermission) {
      return <RiskPermissionEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.riskLimit) {
      return <RiskLimitEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.riskKycPresets) {
      return <RiskKycPresetsEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.riskKycRules) {
      return <RiskKycRulesEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.contentPreview) {
      return <ContentPreviewModal />;
    }
     if (activeModal && activeModal === ModalTypes.customized) {
       return <CmsCustomizedEditModal />;
     }
     if (activeModal && activeModal === ModalTypes.presets) {
       return <PlayersPresetsEditModal />;
    }
    if (activeModal && activeModal === ModalTypes.scheduler) {
      return <SchedulerModal />
    }
  };

  return (
    <BackOfficeRealtimeProvider>
      <div className="layout-wrapper">
        <div className="layout-content">
          <Sidebar />
          <DndProvider backend={HTML5Backend}>
            {renderModal()}
            <div className="layout-content--wrapper">
              <Header />
              {children}
            </div>
          </DndProvider>
        </div>
        <Notifications />
      </div>
    </BackOfficeRealtimeProvider>
  );
};

export default Layout;
